/**
 * A scene's camera (lifted from lantern-water's `main.js` `resize` and its
 * per-frame framing): a fixed offset from an eased target that follows the
 * traveler by the framing's factors (the street framing tracks x at 0.9, the
 * ground's height at 0.8 and y at 0.86 behind a 2 m lead, so the frame stops
 * short at the map's edges and rises over bridges and stairs; the shop's
 * camera barely moves), with the vertical field of view set so the framing's
 * span fills the frame height at the target (wider on portrait screens). The
 * framing presets are the scene's (`camera.presets`). Portrait screens also
 * lead north by `camera.portraitLead` at full portrait (the dungeon's
 * look-ahead rule, `camera.ts`, in metres).
 *
 * Slice 7a (ADR 0033) adds what brad-airship's `airshipCameraFrame` needs,
 * as preset fields: a follow `anchor` (a room's centre: the target moves 7%
 * of the way to the traveler), a span of at least `fitWidth` over the aspect
 * (the room fills the frame's width; landscape only), a `portrait` offset,
 * span and follow below an aspect (its own framing, never width-fitted: the
 * rooms' 20° portrait lens), and `fit`: the overview, which frames the built place's
 * bounds (`setBounds`) by centring their projected box, the prototype's six
 * iterations, and opening the field of view to it with a 4% margin.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { sceneLook } from './constants.ts';

type Preset = Scene['camera']['presets'][string];
type Follow = Preset['follow'];

export type SceneCamera = {
  readonly target: THREE.Vector3;
  /** The look-ahead now, metres. */
  lead(): number;
  /** Sets the lens for the canvas's aspect (the span over the offset's distance). */
  frame(camera: THREE.PerspectiveCamera, aspect: number): void;
  /** Eases the target toward the traveler at (x, y, h) (game axes) by `dt` seconds; `snap` jumps there. */
  follow(camera: THREE.PerspectiveCamera, x: number, y: number, h: number, dt: number, cssWidth: number, snap: boolean): void;
  /** Another of the scene's framings (a test's or a capture's overview); unknown names keep the current one. */
  setFraming(name: string): void;
  readonly framing: string;
  /** The built place's bounds (three's axes), which a `fit` framing frames. */
  setBounds(bounds: THREE.Box3 | null): void;
};

export const createSceneCamera = (scene: Scene): SceneCamera => {
  const c = scene.camera;
  let framing = c.framing;
  const presetOf = (name: string): Preset => {
    const p = c.presets[name];
    if (p === undefined) throw new Error(`${scene.id}: camera framing ${name} has no preset`);
    return p;
  };
  let preset = presetOf(framing);
  const target = new THREE.Vector3();
  const desired = new THREE.Vector3();
  const offset = new THREE.Vector3();
  let aspect = 1;
  let bounds: THREE.Box3 | null = null;
  const fitted = { target: new THREE.Vector3(), fov: 40 };
  const lead = (): number => c.portraitLead * Math.max(0, Math.min(1, (1 - aspect) / c.leadAspectRange));
  const portrait = (): boolean => preset.portrait !== undefined && aspect < preset.portrait.below;
  /** The offset in three's axes now (game: x east, y north, h up → three: x, h, −y). */
  const offsetNow = (): THREE.Vector3 => {
    const o = portrait() && preset.portrait !== undefined ? preset.portrait.offset : preset.offset;
    return offset.set(o.x, o.h, -o.y);
  };
  /** `airshipCameraFrame`'s overview: centre the projected bounds over six iterations, then open the lens to them. */
  const fit = (): void => {
    if (bounds === null) return;
    const b = bounds;
    const o = offsetNow();
    const t = b.getCenter(fitted.target);
    const test = new THREE.PerspectiveCamera(45, aspect, 1, 230);
    const corners: THREE.Vector3[] = [];
    for (const x of [b.min.x, b.max.x]) for (const y of [b.min.y, b.max.y]) for (const z of [b.min.z, b.max.z]) corners.push(new THREE.Vector3(x, y, z));
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    for (let i = 0; i < 6; i += 1) {
      test.position.copy(t).add(o);
      test.lookAt(t);
      test.updateMatrixWorld();
      const points = corners.map((p) => p.clone().applyMatrix4(test.matrixWorldInverse));
      const xs = points.map((p) => p.x / -p.z);
      const ys = points.map((p) => p.y / -p.z);
      t.addScaledVector(right.set(1, 0, 0).applyQuaternion(test.quaternion), (Math.min(...xs) + Math.max(...xs)) * 0.5 * o.length());
      t.addScaledVector(up.set(0, 1, 0).applyQuaternion(test.quaternion), (Math.min(...ys) + Math.max(...ys)) * 0.5 * o.length());
    }
    test.position.copy(t).add(o);
    test.lookAt(t);
    test.updateMatrixWorld();
    let tangent = 0;
    for (const corner of corners) {
      const p = corner.clone().applyMatrix4(test.matrixWorldInverse);
      tangent = Math.max(tangent, Math.abs(p.y) / -p.z, Math.abs(p.x) / -p.z / aspect);
    }
    fitted.fov = THREE.MathUtils.radToDeg(2 * Math.atan(tangent * 1.04));
  };
  const lens = (camera: THREE.PerspectiveCamera): void => {
    camera.aspect = aspect;
    if (preset.fit === true && bounds !== null) {
      fit();
      camera.fov = fitted.fov;
    } else {
      const distance = offsetNow().length();
      const p = preset.portrait;
      const own = portrait() && p !== undefined;
      let h = own ? p.span : aspect < 1 ? preset.span * preset.portraitSpan : preset.span;
      // The width fit is the landscape framing's; a portrait preset is its own constraint (`airshipCameraFrame`'s
      // portrait camera: its own offset and a fixed 20° lens that follows the traveler, never widened to the room).
      if (preset.fitWidth !== undefined && !own) h = Math.max(h, preset.fitWidth / aspect);
      camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(h / (2 * distance)));
    }
    camera.near = c.near;
    camera.far = c.far;
    camera.updateProjectionMatrix();
  };
  let lastCamera: THREE.PerspectiveCamera | null = null;
  return {
    target,
    lead,
    frame(camera, nextAspect) {
      aspect = nextAspect;
      lastCamera = camera;
      lens(camera);
    },
    follow(camera, x, y, h, dt, cssWidth, snap) {
      const o = offsetNow();
      if (preset.fit === true && bounds !== null) {
        desired.copy(fitted.target);
      } else {
        const f: Follow = portrait() && preset.portrait !== undefined ? preset.portrait.follow : preset.follow;
        const ax = f.anchor?.x ?? 0;
        const ay = f.anchor?.y ?? 0;
        const ty = ay + (y - ay) * f.y + f.yOffset + lead();
        desired.set(ax + (x - ax) * (cssWidth < sceneLook.narrowWidth ? f.xNarrow : f.x), f.base + h * f.h, -ty);
      }
      if (snap || preset.damping <= 0) target.copy(desired);
      else target.lerp(desired, 1 - Math.exp(-dt * preset.damping));
      camera.position.copy(target).add(o);
      camera.lookAt(target);
      camera.updateMatrixWorld();
    },
    setFraming(name) {
      if (c.presets[name] === undefined || name === framing) return;
      framing = name;
      preset = presetOf(name);
      if (lastCamera !== null) lens(lastCamera);
    },
    get framing() {
      return framing;
    },
    setBounds(next) {
      bounds = next;
      if (lastCamera !== null) lens(lastCamera);
    },
  };
};
